export const MIN_HOUR = 0;
export const MAX_HOUR = 23;
export const DEFAULT_MAX_MESSAGES = 10;

export const blankDocument = () => ({ id: null, title: '', body: '' });

export const emptyAiAgentForm = () => ({
  name: '',
  inboxId: '',
  instructions: '',
  active: true,
  autoReply: true,
  activeFromHour: '',
  activeToHour: '',
  audienceLabels: [],
  forbiddenTopics: [],
  maxMessagesPerConversation: DEFAULT_MAX_MESSAGES,
  fallbackTemplateName: '',
  fallbackTemplateVariables: [],
  documents: [],
});

const toHourInput = value =>
  value === null || value === undefined ? '' : String(value);

export const aiAgentToForm = agent => ({
  name: agent.name || '',
  inboxId: agent.inbox_id ? String(agent.inbox_id) : '',
  instructions: agent.instructions || '',
  active: agent.active !== false,
  autoReply: agent.auto_reply === true,
  activeFromHour: toHourInput(agent.active_from_hour),
  activeToHour: toHourInput(agent.active_to_hour),
  audienceLabels: [...(agent.audience_labels || [])],
  forbiddenTopics: [...(agent.forbidden_topics || [])],
  maxMessagesPerConversation:
    agent.max_messages_per_conversation ?? DEFAULT_MAX_MESSAGES,
  fallbackTemplateName: agent.fallback_template_name || '',
  fallbackTemplateVariables: [...(agent.fallback_template_variables || [])],
  documents: (agent.documents || []).map(document => ({
    id: document.id,
    title: document.title,
    body: document.body || '',
  })),
});

export const isBlankDocument = document =>
  !String(document.title ?? '').trim() && !String(document.body ?? '').trim();

export const parseHour = value => {
  const raw = String(value ?? '').trim();
  return raw === '' ? null : Number(raw);
};

export const validateAiAgentForm = form => {
  const errors = {};

  if (!String(form.name ?? '').trim()) errors.name = 'required';

  const maxMessages = Number(form.maxMessagesPerConversation);
  if (!Number.isInteger(maxMessages) || maxMessages <= 0)
    errors.maxMessagesPerConversation = 'invalid';

  const from = String(form.activeFromHour ?? '').trim();
  const to = String(form.activeToHour ?? '').trim();
  if ((from === '') !== (to === '')) {
    errors.activeFromHour = 'paired';
    errors.activeToHour = 'paired';
  } else if (from !== '') {
    [
      ['activeFromHour', from],
      ['activeToHour', to],
    ].forEach(([field, value]) => {
      const hour = Number(value);
      if (!Number.isInteger(hour) || hour < MIN_HOUR || hour > MAX_HOUR)
        errors[field] = 'range';
    });
  }

  form.documents.forEach((document, index) => {
    if (!isBlankDocument(document) && !String(document.title ?? '').trim())
      errors[`document-${index}`] = 'title-required';
  });

  return errors;
};

export const buildAiAgentPayload = (form, removedDocumentIds = []) => {
  const documents = [];

  form.documents.forEach(document => {
    if (isBlankDocument(document)) {
      // Clearing an existing document sends a delete so the stored row goes away.
      if (document.id) documents.push({ id: document.id, _destroy: true });
      return;
    }

    documents.push({
      ...(document.id ? { id: document.id } : {}),
      title: String(document.title).trim(),
      body: String(document.body ?? ''),
    });
  });

  removedDocumentIds.forEach(id => documents.push({ id, _destroy: true }));

  return {
    name: String(form.name).trim(),
    inbox_id: form.inboxId ? Number(form.inboxId) : null,
    instructions: form.instructions ?? '',
    active: form.active,
    auto_reply: form.autoReply,
    active_from_hour: parseHour(form.activeFromHour),
    active_to_hour: parseHour(form.activeToHour),
    audience_labels: [...form.audienceLabels],
    forbidden_topics: [...form.forbiddenTopics],
    max_messages_per_conversation: Number(form.maxMessagesPerConversation),
    fallback_template_name:
      String(form.fallbackTemplateName ?? '').trim() || null,
    fallback_template_variables: [...form.fallbackTemplateVariables],
    ai_documents_attributes: documents,
  };
};
